import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Builds an aggregate MeSH category tree from a { uniprotId: [path, ...] } index.
 * Returns { treeRoot, uniprotsByNode } where:
 *   treeRoot — nested { name, children: Map }
 *   uniprotsByNode — Map<nodeKey, Set<uniprotId>>
 */
function buildAggregateTree(index, knownIds) {
  // nodeKey = segments joined by '|||'
  const uniprotsByNode = new Map();
  const root = { name: '__root__', children: new Map() };

  for (const [uniprotId, paths] of Object.entries(index)) {
    // Skip proteins not present in the database
    if (knownIds && !knownIds.has(uniprotId)) continue;
    for (const pathStr of paths) {
      const parts = pathStr.split(/\s*->\s*/).map(s => s.trim()).filter(Boolean);
      if (parts.length === 0) continue;

      // For each prefix of the path, register this protein under that node key
      let keyParts = [];
      let node = root;
      for (const part of parts) {
        keyParts = [...keyParts, part];
        const key = keyParts.join('|||');
        if (!uniprotsByNode.has(key)) uniprotsByNode.set(key, new Set());
        uniprotsByNode.get(key).add(uniprotId);
        if (!node.children.has(part)) {
          node.children.set(part, { name: part, children: new Map() });
        }
        node = node.children.get(part);
      }
    }
  }

  // Convert map-based tree to array-based, sorted alphabetically
  const toPlain = (node, keyParts = []) => {
    const children = Array.from(node.children.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, child]) => toPlain(child, [...keyParts, child.name]));
    return {
      name: node.name,
      key: keyParts.join('|||'),
      children,
    };
  };

  // Top-level nodes (children of __root__)
  const topLevel = Array.from(root.children.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, child]) => toPlain(child, [child.name]));

  return { topLevel, uniprotsByNode };
}

/**
 * Renders one node in the MeSH tree.
 */
function TreeNode({ node, depth, selectedKey, onSelect, uniprotsByNode, totalProteins }) {
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = node.children && node.children.length > 0;
  const count = uniprotsByNode.get(node.key)?.size ?? 0;
  const isSelected = selectedKey === node.key;

  const indent = depth * 18;

  return (
    <div>
      <div
        className={`mesh-tree-node${isSelected ? ' mesh-tree-node--selected' : ''}`}
        style={{ paddingLeft: indent + 4 }}
        onClick={() => {
          if (hasChildren) setExpanded(e => !e);
          onSelect(isSelected ? null : node.key);
        }}
        title={node.name}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (hasChildren) setExpanded(ex => !ex);
            onSelect(isSelected ? null : node.key);
          }
        }}
      >
        <span className="mesh-tree-toggle">
          {hasChildren ? (expanded ? '▾' : '▸') : ''}
        </span>
        <span className="mesh-tree-label">{node.name}</span>
        <span className="mesh-tree-count">{count}</span>
      </div>
      {hasChildren && expanded && (
        <div>
          {node.children.map(child => (
            <TreeNode
              key={child.key}
              node={child}
              depth={depth + 1}
              selectedKey={selectedKey}
              onSelect={onSelect}
              uniprotsByNode={uniprotsByNode}
              totalProteins={totalProteins}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * MeshTreeFilter component.
 *
 * Props:
 *   respiratoryOnly {boolean}          — whether to show only respiratory-condition tree
 *   onFilterChange(Set|null) — called with Set of matching uniprotIds (null = no filter)
 *   isDark {boolean}
 */
export default function MeshTreeFilter({ respiratoryOnly, onRespiratoryOnlyChange, onFilterChange, isDark, knownIds }) {
  const [meshIndex, setMeshIndex] = useState(null);
  const [respIndex, setRespIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [hierarchyFilterMode, setHierarchyFilterMode] = useState('selected'); // 'all' | 'selected'
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  // Fetch both indexes once
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${process.env.PUBLIC_URL}/data/mesh-index.json`).then(r => r.json()),
      fetch(`${process.env.PUBLIC_URL}/data/mesh-respiratory-index.json`).then(r => r.json()),
    ]).then(([mi, ri]) => {
      if (cancelled) return;
      setMeshIndex(mi);
      setRespIndex(ri);
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load MeSH index:', err);
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const activeIndex = respiratoryOnly ? respIndex : meshIndex;

  const lowerSearch = searchText.toLowerCase();

  // When a search is active, restrict the index to only paths that contain the search term.
  // This ensures both the tree structure and protein counts are scoped to matching paths.
  const effectiveIndex = useMemo(() => {
    if (!activeIndex || !lowerSearch) return activeIndex;
    const result = {};
    for (const [uniprotId, paths] of Object.entries(activeIndex)) {
      const matchingPaths = paths.filter(path => path.toLowerCase().includes(lowerSearch));
      if (matchingPaths.length > 0) result[uniprotId] = matchingPaths;
    }
    return result;
  }, [activeIndex, lowerSearch]);

  const { topLevel, uniprotsByNode } = useMemo(() => {
    if (!effectiveIndex) return { topLevel: [], uniprotsByNode: new Map() };
    return buildAggregateTree(effectiveIndex, knownIds);
  }, [effectiveIndex, knownIds]);

  // All proteins visible in the current displayed tree
  const allDisplayedProteins = useMemo(() => {
    if (!effectiveIndex) return new Set();
    const s = new Set();
    for (const uid of Object.keys(effectiveIndex)) {
      if (!knownIds || knownIds.has(uid)) s.add(uid);
    }
    return s;
  }, [effectiveIndex, knownIds]);

  // Reset selection when respiratory mode changes
  useEffect(() => {
    setSelectedKey(null);
    setSearchText('');
  }, [respiratoryOnly]);

  // Notify parent when selection or hierarchy filter mode changes
  useEffect(() => {
    if (!selectedKey) {
      onFilterChangeRef.current(hierarchyFilterMode === 'all' ? allDisplayedProteins : null);
      return;
    }
    const set = uniprotsByNode.get(selectedKey);
    onFilterChangeRef.current(set || new Set());
  }, [selectedKey, uniprotsByNode, hierarchyFilterMode, allDisplayedProteins]);

  const handleSelect = (key) => {
    setSelectedKey(key);
  };

  const handleReset = () => {
    setSelectedKey(null);
    setSearchText('');
  };

  const selectedCount = selectedKey
    ? (uniprotsByNode.get(selectedKey)?.size ?? 0)
    : (hierarchyFilterMode === 'all' ? allDisplayedProteins.size : null);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '12px', opacity: 0.7, fontSize: '0.9rem' }}>
        Loading MeSH tree…
      </div>
    );
  }

  if (!activeIndex) {
    return (
      <div style={{ textAlign: 'center', padding: '12px', opacity: 0.7, fontSize: '0.9rem' }}>
        MeSH data unavailable.
      </div>
    );
  }

  return (
    <div className="mesh-tree-filter" data-dark={isDark ? '1' : '0'}>
      <div className="mesh-tree-toolbar">
        <span className="material-symbols-outlined icon" style={{ fontSize: 22, verticalAlign: 'middle' }}>
          account_tree
        </span>
        <input
          className="mesh-tree-search"
          type="text"
          placeholder="Search MeSH categories…"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          aria-label="Search MeSH tree"
        />
        {selectedCount !== null && (
          <span className="mesh-tree-selection-label">
            {selectedCount} protein{selectedCount === 1 ? '' : 's'} matched
          </span>
        )}
        <button
          type="button"
          className="mesh-tree-reset"
          onClick={handleReset}
          title="Clear MeSH filter"
          disabled={!selectedKey && !searchText}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 22, verticalAlign: 'middle' }}>
            autorenew
          </span>
        </button>
      </div>
      <div className="mesh-tree-col-header">
        <span className="mesh-tree-col-header-label">Medical Condition</span>
        <span className="mesh-tree-col-header-count">Proteins</span>
      </div>
      <div className="mesh-tree-scroll">
        {topLevel.length === 0 ? (
          <div style={{ padding: '8px 12px', opacity: 0.6, fontSize: '0.88rem' }}>
            No matching categories.
          </div>
        ) : (
          topLevel.map(node => (
            <TreeNode
              key={node.key}
              node={node}
              depth={0}
              selectedKey={selectedKey}
              onSelect={handleSelect}
              uniprotsByNode={uniprotsByNode}
              totalProteins={Object.keys(activeIndex).length}
            />
          ))
        )}
      </div>
      <div style={{ padding: '6px 8px 4px 8px', borderTop: '1px solid rgba(128,128,128,0.25)', display: 'flex', gap: 0 }}>
        <button
          type="button"
          onClick={() => setHierarchyFilterMode('all')}
          style={{
            flex: 1,
            padding: '5px 8px',
            fontSize: '0.82rem',
            cursor: 'pointer',
            border: '1px solid rgba(128,128,128,0.4)',
            borderRadius: '16px 0 0 16px',
            background: hierarchyFilterMode === 'all' ? 'rgba(70,130,180,0.75)' : 'transparent',
            color: hierarchyFilterMode === 'all' ? '#fff' : 'inherit',
          }}
        >
          Apply filter immediately
        </button>
        <button
          type="button"
          onClick={() => setHierarchyFilterMode('selected')}
          style={{
            flex: 1,
            padding: '5px 8px',
            fontSize: '0.82rem',
            cursor: 'pointer',
            border: '1px solid rgba(128,128,128,0.4)',
            borderLeft: 'none',
            borderRadius: '0 16px 16px 0',
            background: hierarchyFilterMode === 'selected' ? 'rgba(70,130,180,0.75)' : 'transparent',
            color: hierarchyFilterMode === 'selected' ? '#fff' : 'inherit',
          }}
        >
          Apply selected hierarchies only
        </button>
      </div>
      <div style={{ padding: '4px 8px 8px 8px' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={respiratoryOnly}
            onChange={e => onRespiratoryOnlyChange && onRespiratoryOnlyChange(e.target.checked)}
          />
          Show filters for respiratory related conditions only
        </label>
      </div>
    </div>
  );
}
